import { ERROR_MESSAGES } from "@/constants/error-messages";
import { MAX_MESSAGE_LENGTH } from "@/constants/search-constants";
import { useCallback, useState } from "react";

type UseLimitedInputOptions = {
  maxLength?: number;
  showError?: boolean;
};

export function useLimitedInput(
  initialValue: string = "",
  options: UseLimitedInputOptions = {},
) {
  const { maxLength = MAX_MESSAGE_LENGTH, showError = false } = options;

  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;

    if (showError) {
      // For QueryInput: show error but allow typing
      setValue(newValue);
      setError(newValue.length > maxLength ? ERROR_MESSAGES.INPUT_QUERY_TOO_LONG : null);
    }
    else {
      // For search boxes: just truncate
      setValue(newValue.slice(0, maxLength));
    }
  }, [maxLength, showError]);

  const setLimitedValue = useCallback((val: string) => {
    if (showError) {
      setValue(val);
      setError(val.length > maxLength ? ERROR_MESSAGES.INPUT_QUERY_TOO_LONG : null);
    }
    else {
      setValue(val.slice(0, maxLength));
    }
  }, [maxLength, showError]);

  const reset = useCallback(() => {
    setValue(initialValue);
    setError(null);
  }, [initialValue]);

  return {
    value,
    error,
    handleChange,
    setValue: setLimitedValue,
    reset,
    isValid: value.length <= maxLength,
  };
}
